/**
 * Welle Z W8 (Entscheidung 6: "Bestandskunden bleiben wie sie sind. Beim
 * Import kommt ein Bestätigungssatz dazu.") — the legal-basis confirmation an
 * organiser gives before a file of people is imported.
 *
 * Three doors ask for it, with ONE sentence:
 *
 *  - the guest import wizard (PImport → PImportCore)       → source `WIZARD`
 *  - the CRM contact import (PContactImport → PImportCore)  → `CONTACT_WIZARD`
 *  - Velo's spreadsheet import card (ConfirmationCard)      → `AI_CARD`
 *
 * Wire contract (main-backend `ImportLegalBasis`, `OptionalParams`): five
 * fields in `optionalParams` of the import job — IMPORT_GUESTS_CREATE /
 * IMPORT_GUESTS_OVERWRITE on `POST /background-jobs/{eventId}`,
 * CONTACT_IMPORT_CREATE on `POST /background-jobs/company/{companyId}`. The
 * backend writes ONE `IMPORT_LEGAL_BASIS` audit row per accepted import job,
 * CONFIRMED or NOT_CONFIRMED, and never refuses the import on it; it records
 * the KEY, the version token and the locale — never the sentence. A malformed
 * key / version / locale is dropped there, not refused.
 *
 * The cockpit sends the fields only with the tick: every door is locked until
 * the box is ticked, so an import from this bundle always carries them.
 */

/** i18n key of the sentence — one key for guest and contact import. */
export const IMPORT_LEGAL_BASIS_TEXT_KEY = "importPopup.legalBasisConfirm";

/**
 * Audit r1 C6 (SMS channel wave): the sentence when the file maps the SMS
 * opt-in column — the e-mail sentence plus one confirming that every guest
 * marked "Yes" there agreed to receive SMS. Its own key and version token: the
 * backend records which sentence was ticked.
 */
export const IMPORT_LEGAL_BASIS_SMS_TEXT_KEY = "importPopup.legalBasisConfirmSms";

/**
 * Version token of the sentence (backend shape `[A-Za-z0-9._-]{1,40}`). A
 * change of meaning gets a new token here. Never part of the bundle text
 * (pinned by importLegalBasis.i18n.test.ts) — a copy in 57 files would drift.
 */
export const IMPORT_LEGAL_BASIS_TEXT_VERSION = "import-legal-basis-v1";

/** Version token of the SMS sentence (same shape rule). */
export const IMPORT_LEGAL_BASIS_SMS_TEXT_VERSION = "import-legal-basis-sms-v1";

/** The sentence's key: the SMS one when the import maps the SMS opt-in column. */
export function importLegalBasisTextKey(options?: { sms?: boolean }): string {
  return options?.sms ? IMPORT_LEGAL_BASIS_SMS_TEXT_KEY : IMPORT_LEGAL_BASIS_TEXT_KEY;
}

export type ImportLegalBasisSource = "WIZARD" | "CONTACT_WIZARD" | "AI_CARD";

/** The five optionalParams fields of a confirmed import. */
export interface ImportLegalBasisParams {
  legalBasisConfirmed: true;
  legalBasisTextKey: string;
  legalBasisTextVersion: string;
  /** BCP-47 tag of the UI language the sentence was shown in. */
  legalBasisLocale: string;
  legalBasisSource: ImportLegalBasisSource;
}

/**
 * What the ConfirmationCard hands to the import tool's handler when the
 * organiser ticked the box (ai-tool-bridge `AiClientConfirmation`). Built by
 * the CARD from the constants above and the UI locale — never parsed from the
 * tool input the model wrote.
 */
export interface ImportLegalBasisConfirmation {
  legalBasisConfirmed: true;
  textKey: string;
  textVersion: string;
  locale: string;
}

/** The card's confirmation object for the current UI locale. */
export function importLegalBasisConfirmation(
  locale: string,
): ImportLegalBasisConfirmation {
  return {
    legalBasisConfirmed: true,
    textKey: IMPORT_LEGAL_BASIS_TEXT_KEY,
    textVersion: IMPORT_LEGAL_BASIS_TEXT_VERSION,
    locale,
  };
}

/** The wire fields of a ticked wizard import (`sms`: the file maps the SMS opt-in column). */
export function importLegalBasisParams(
  source: ImportLegalBasisSource,
  locale: string,
  options?: { sms?: boolean },
): ImportLegalBasisParams {
  return {
    legalBasisConfirmed: true,
    legalBasisTextKey: importLegalBasisTextKey(options),
    legalBasisTextVersion: options?.sms ? IMPORT_LEGAL_BASIS_SMS_TEXT_VERSION : IMPORT_LEGAL_BASIS_TEXT_VERSION,
    legalBasisLocale: locale,
    legalBasisSource: source,
  };
}

/**
 * The wire fields for a card confirmation, or null when there is none — or
 * the tick in it is anything but a literal `true` (the handler then refuses
 * softly and submits nothing).
 */
export function importLegalBasisParamsFromConfirmation(
  confirmation: unknown,
): ImportLegalBasisParams | null {
  if (!confirmation || typeof confirmation !== "object") return null;
  const c = confirmation as Partial<Record<keyof ImportLegalBasisConfirmation, unknown>>;
  if (c.legalBasisConfirmed !== true) return null;
  return {
    legalBasisConfirmed: true,
    legalBasisTextKey:
      typeof c.textKey === "string" ? c.textKey : IMPORT_LEGAL_BASIS_TEXT_KEY,
    legalBasisTextVersion:
      typeof c.textVersion === "string"
        ? c.textVersion
        : IMPORT_LEGAL_BASIS_TEXT_VERSION,
    legalBasisLocale: typeof c.locale === "string" ? c.locale : "",
    legalBasisSource: "AI_CARD",
  };
}
